/**
 * SectionCard — Card with a dark-themed header strip
 *
 * Provides visual contrast like the old HighCall app:
 * dark blue header bar + white body content.
 */

import { ChevronRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';

interface SectionCardProps {
  title: string;
  headerRight?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  /**
   * Turn the header strip into a toggle. The body is UNMOUNTED while closed —
   * not just hidden — so a section whose content is expensive to load fetches
   * nothing until someone opens it.
   *
   * Controlled: pass `open` and `onOpenChange` together.
   */
  collapsible?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Shown in the header strip beside the title, open or closed. */
  subtitle?: React.ReactNode;
}

export function SectionCard({
  title,
  headerRight,
  children,
  className,
  collapsible = false,
  open = true,
  onOpenChange,
  subtitle,
}: SectionCardProps) {
  const isOpen = collapsible ? open : true;

  const heading = (
    <span className="flex items-center gap-2 min-w-0">
      {collapsible && (
        <ChevronRight
          className={`h-4 w-4 shrink-0 transition-transform duration-200 ${
            isOpen ? 'rotate-90' : ''
          }`}
          aria-hidden="true"
        />
      )}
      <span className="text-sm font-semibold truncate">{title}</span>
      {subtitle && <span className="text-xs font-normal opacity-80 truncate">{subtitle}</span>}
    </span>
  );

  return (
    <Card className={`overflow-hidden ${className ?? ''}`}>
      {/* Dark header strip */}
      <div
        className="flex items-center justify-between gap-3 px-5 py-2.5 min-h-[40px]"
        style={{
          backgroundColor: 'hsl(var(--header-background))',
          color: 'hsl(var(--header-foreground))',
        }}
      >
        {collapsible ? (
          // The toggle is the title only — `headerRight` holds its own
          // controls (a filter, a button) and must not sit inside a button.
          <button
            type="button"
            onClick={() => onOpenChange?.(!isOpen)}
            aria-expanded={isOpen}
            className="flex min-w-0 flex-1 items-center text-left"
          >
            <h2 className="min-w-0">{heading}</h2>
          </button>
        ) : (
          <h2 className="text-sm font-semibold">{title}</h2>
        )}
        {headerRight}
      </div>
      {/* White body */}
      {isOpen && <CardContent className="p-5">{children}</CardContent>}
    </Card>
  );
}
